import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import {
  ChevronRight, ChevronLeft, ArrowRight, Loader2,
  Bot, BrainCircuit, MessageSquare, Mic, Network, Sliders,
  Building2, ShoppingCart, HeartPulse, Scale, Briefcase, GraduationCap,
  Zap, ShieldCheck, Globe, Cpu, CheckCircle2,
  Database, Search, Layers, Code2, Smartphone, Palette, TestTube2,
  FileText, BarChart3, Settings, Users, Lock, Repeat, Workflow,
  PenTool, Monitor, Server, Cloud, Truck, Boxes,
} from 'lucide-react';

import PageHero from '@/components/layout/PageHero';
import SectionHeading from '@/components/brand/SectionHeading';
import MetricCountUp from '@/components/brand/MetricCountUp';
import CtaBand from '@/components/sections/CtaBand';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, staggerParent, cardPop, viewportOnce } from '@/lib/motion';
import { useServiceDetail } from '@/hooks/useServiceDetail';
import NotFound from '@/pages/NotFound';

const EASE = [0.22, 1, 0.36, 1];
const MotionLink = motion.create(Link);

function BlueAnimatedLink({ to, children }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 220, damping: 18, mass: 0.5 });
  const y = useSpring(my, { stiffness: 220, damping: 18, mass: 0.5 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(Math.max(-6, Math.min(6, (e.clientX - (r.left + r.width / 2)) * 0.18)));
    my.set(Math.max(-4, Math.min(4, (e.clientY - (r.top + r.height / 2)) * 0.22)));
  };
  const onLeave = () => { mx.set(0); my.set(0); };
  return (
    <MotionLink
      to={to}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 360, damping: 22 }}
      className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-7 py-3 text-sm font-semibold text-white bg-brandblue hover:bg-[#1a4d80] shadow-[0_4px_20px_rgba(47,111,176,0.40)] hover:shadow-[0_18px_44px_rgba(47,111,176,0.50)] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue/60"
    >
      <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/2 w-full bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.28)_50%,transparent_75%)] opacity-0 group-hover:opacity-100 group-hover:animate-[sheen_900ms_ease-in-out]"
      />
    </MotionLink>
  );
}

/* ─── All 12 services — used for Related Services navigation ────────────── */
const ALL_SERVICES = [
  { slug: 'generative-ai-chatbots',    title: 'Generative AI & Chatbots',           eyebrow: 'AI Engineering',       icon: 'Bot'         },
  { slug: 'machine-learning',          title: 'Machine Learning & Predictive AI',    eyebrow: 'Data Science',         icon: 'BrainCircuit'},
  { slug: 'nlp-computer-vision',       title: 'NLP & Computer Vision',               eyebrow: 'Intelligent Systems',  icon: 'MessageSquare'},
  { slug: 'workflow-automation',       title: 'Workflow Automation',                 eyebrow: 'Process AI',           icon: 'Workflow'    },
  { slug: 'ai-integration-consulting', title: 'AI Integration & Consulting',         eyebrow: 'Strategy',             icon: 'Network'     },
  { slug: 'web-mobile-development',    title: 'Web & Mobile Development',            eyebrow: 'Product Engineering',  icon: 'Smartphone'  },
  { slug: 'erp-solutions',             title: 'ERP Solutions',                       eyebrow: 'Enterprise Software',  icon: 'Boxes'       },
  { slug: 'ui-ux-design',              title: 'UI/UX Design',                        eyebrow: 'Product Design',       icon: 'Palette'     },
  { slug: 'rag-systems',               title: 'RAG Systems',                         eyebrow: 'Knowledge AI',         icon: 'Database'    },
  { slug: 'ai-agents',                 title: 'AI Agents & Agentic Systems',         eyebrow: 'Autonomous AI',        icon: 'Network'     },
  { slug: 'testing-qa',                title: 'Testing & Quality Assurance',         eyebrow: 'Quality Engineering',  icon: 'TestTube2'   },
  { slug: 'blockchain',                title: 'Blockchain & Web3',                   eyebrow: 'Decentralised Tech',   icon: 'Lock'        },
];

function getRelated(currentSlug, count = 3) {
  const others = ALL_SERVICES.filter((s) => s.slug !== currentSlug);
  const idx    = ALL_SERVICES.findIndex((s) => s.slug === currentSlug);
  const start  = ((idx < 0 ? 0 : idx) + 1) % others.length;
  const result = [];
  for (let i = 0; i < count; i++) result.push(others[(start + i) % others.length]);
  return result;
}

/* ─── All default page data (CMS overrides each field) ──────────────────── */

const DEFAULTS = {
  /* ── 1. Generative AI & Chatbots ─────────────────────────────────────── */
  'generative-ai-chatbots': {
    title: 'Generative AI & Chatbots',
    crumb: 'Generative AI',
    tagline:
      'Build intelligent AI systems that understand, converse, and act — powered by GPT-4o, Claude 3.5, Gemini 2.0, and leading open-source models. Production-grade. Deployed worldwide.',
    hero_image:
      '/images/services/hero/photo-1677442135703-1787eea5ce01.jpg',
    overview_heading: 'AI that thinks, talks, and delivers — at scale',
    overview_body:
      'We design and ship production-grade generative AI applications and intelligent chatbot systems that transform how businesses interact with customers, automate workflows, and unlock the value hidden in unstructured data.\n\nFrom GPT-4o-powered copilots to Retrieval-Augmented Generation (RAG) pipelines, autonomous agentic systems, and omnichannel voice agents — our engineering team has delivered 50+ AI products across finance, healthcare, e-commerce, legal, and enterprise operations.\n\nEvery system we build is grounded in real-world performance: hallucination-tested, latency-optimised, and built for the scale your business demands.',
    metrics: [
      { value: '50+',  label: 'AI Products Delivered' },
      { value: '40+',  label: 'Countries Served' },
      { value: '10×',  label: 'Faster Knowledge Retrieval' },
      { value: '20',   label: 'Years of Engineering Excellence' },
    ],
    features: [
      {
        icon: 'Bot',
        title: 'Custom LLM Applications',
        desc: 'Tailored applications powered by GPT-4o, Claude 3.5, and Gemini 2.0 — from intelligent copilots to document processors, code assistants, and autonomous decision engines.',
        accent: '#10A37F',
      },
      {
        icon: 'BrainCircuit',
        title: 'RAG Systems',
        desc: 'Retrieval-Augmented Generation pipelines that connect LLMs to your private knowledge base — delivering accurate, grounded, hallucination-free responses from your own data.',
        accent: '#2F6FB0',
      },
      {
        icon: 'MessageSquare',
        title: 'Conversational Chatbots',
        desc: 'Omnichannel AI chatbots deployed on web, WhatsApp, Slack, and Microsoft Teams — handling customer support, sales qualification, and internal operations 24/7.',
        accent: '#C9962E',
      },
      {
        icon: 'Mic',
        title: 'AI Voice Agents',
        desc: 'Voice-enabled AI assistants built on Whisper and ElevenLabs for customer service calls, IVR automation, and hands-free enterprise workflows.',
        accent: '#7C3AED',
      },
      {
        icon: 'Network',
        title: 'Agentic AI Systems',
        desc: 'Autonomous multi-agent pipelines using LangGraph and AutoGen that reason, plan, and execute complex multi-step tasks without human intervention.',
        accent: '#DC2626',
      },
      {
        icon: 'Sliders',
        title: 'Fine-tuning & Alignment',
        desc: 'Custom model fine-tuning on domain-specific datasets using LoRA and RLHF — adapting foundation models to your exact business vocabulary, tone, and compliance requirements.',
        accent: '#059669',
      },
    ],
    process_steps: [
      {
        step: '01',
        title: 'Discovery & Strategy',
        desc: 'We audit your data landscape, map AI use cases, and define success metrics. We recommend the right LLM stack, embedding model, vector database, and deployment strategy for your exact scenario.',
      },
      {
        step: '02',
        title: 'Architecture & Design',
        desc: 'We design the full AI system architecture — RAG pipeline, agent graph, API layer, integration touchpoints — and document the data flow before writing a single line of production code.',
      },
      {
        step: '03',
        title: 'Build, Test & Tune',
        desc: 'Our engineers build and iterate on the AI system. We evaluate with real-world queries, benchmark hallucination rates, measure latency under load, and optimise until it meets the bar you expect.',
      },
      {
        step: '04',
        title: 'Deploy & Monitor',
        desc: 'We deploy to your cloud (AWS, Azure, GCP) or on-premise infrastructure, wire up observability dashboards, feedback loops, and alerting — so the AI keeps improving post-launch.',
      },
    ],
    use_cases: [
      {
        icon: 'MessageSquare',
        industry: 'Customer Support Automation',
        desc: 'Deflect 70% of tier-1 support tickets with an AI agent that resolves instantly and escalates intelligently — across chat, email, and voice.',
      },
      {
        icon: 'Briefcase',
        industry: 'Sales & Lead Qualification',
        desc: 'An AI SDR that qualifies inbound leads, answers product questions, handles objections, and books meetings — working around the clock.',
      },
      {
        icon: 'Building2',
        industry: 'Enterprise Knowledge Base',
        desc: 'An internal AI assistant that searches your policies, SOPs, HR docs, and technical documentation in seconds — no more Ctrl+F chaos.',
      },
      {
        icon: 'HeartPulse',
        industry: 'Healthcare Patient Assistant',
        desc: 'HIPAA-aware chatbot for appointment scheduling, symptom pre-screening, medication reminders, and compassionate patient triage.',
      },
      {
        icon: 'Scale',
        industry: 'Legal Document Analysis',
        desc: 'AI-powered contract review, clause extraction, and risk flagging — reducing document review time from days to minutes.',
      },
      {
        icon: 'ShoppingCart',
        industry: 'E-commerce Shopping Assistant',
        desc: 'Personalised product discovery, real-time inventory answers, order tracking, and seamless returns — all through a conversational interface.',
      },
    ],
    tech_stack: [
      { name: 'OpenAI GPT-4o',   color: '#10A37F' },
      { name: 'Claude 3.5',      color: '#D4734A' },
      { name: 'Gemini 2.0',      color: '#4285F4' },
      { name: 'LangChain',       color: '#1C3B3A' },
      { name: 'LangGraph',       color: '#1C3B3A' },
      { name: 'Pinecone',        color: '#1A2364' },
      { name: 'Weaviate',        color: '#00B08D' },
      { name: 'HuggingFace',     color: '#FF9D00' },
      { name: 'FastAPI',         color: '#009688' },
      { name: 'Next.js',         color: '#000000' },
      { name: 'AWS Bedrock',     color: '#FF9900' },
      { name: 'Azure OpenAI',    color: '#0078D4' },
    ],
    benefits: [
      {
        icon: 'Zap',
        title: 'Production-grade, not prototypes',
        desc: 'We build AI that scales to millions of queries, handles edge cases, and maintains consistent quality — not demo-ware that breaks at 100 users.',
      },
      {
        icon: 'ShieldCheck',
        title: 'Data privacy by design',
        desc: 'RAG over your private data, on-premise deployment options, and zero data-retention agreements with LLM providers — your data stays yours.',
      },
      {
        icon: 'Globe',
        title: 'Model-agnostic engineering',
        desc: 'Not locked to one provider. We evaluate and pick the best model for your use case, budget, and latency requirements — and we switch when something better ships.',
      },
      {
        icon: 'Cpu',
        title: 'End-to-end ownership',
        desc: 'From discovery and architecture through deployment and ongoing monitoring — one team, full accountability, no handoff chaos between strategy and delivery.',
      },
    ],
    metaTitle: 'Generative AI & Chatbots Development — Max Global Software',
    metaDescription:
      'Build production-grade generative AI applications, RAG systems, and intelligent chatbots with Max Global Software. GPT-4o, Claude, Gemini, LangChain — deployed globally.',
  },

  /* ── 2. Machine Learning & Predictive Analytics ───────────────────────── */
  'machine-learning': {
    title: 'Machine Learning & Predictive Analytics',
    crumb: 'Machine Learning',
    tagline:
      'Turn your data into a competitive advantage. We build, train, and deploy custom machine learning models that predict outcomes, surface hidden patterns, and drive smarter decisions — at production scale.',
    hero_image: '/images/services/hero/photo-1555255707-c07966088b7b.jpg',
    overview_heading: 'From raw data to reliable predictions — end-to-end',
    overview_body:
      'Machine learning is only valuable when it reaches production. Our team handles the full lifecycle: data auditing, feature engineering, model selection, training, evaluation, and deployment onto scalable cloud infrastructure.\n\nWe build regression, classification, clustering, and deep learning models using Python-native tooling — and we pair every model with the observability layer needed to catch drift before it hurts your business.\n\nFrom demand forecasting for global retailers to churn prediction for SaaS companies and fraud detection for fintech — our models have processed billions of events and saved clients millions in operational waste.',
    metrics: [
      { value: '200M+', label: 'Predictions Served Daily' },
      { value: '92%',   label: 'Average Model Accuracy' },
      { value: '3×',    label: 'Faster Time to Insight' },
      { value: '40+',   label: 'ML Models in Production' },
    ],
    features: [
      { icon: 'BarChart3',  title: 'Predictive Modelling',          desc: 'Regression and classification models that forecast demand, revenue, churn, and risk — trained on your historical data and continuously monitored in production.', accent: '#2F6FB0' },
      { icon: 'Network',    title: 'Recommendation Engines',         desc: 'Collaborative and content-based filtering systems that personalise product recommendations, content feeds, and search results at scale.', accent: '#10A37F' },
      { icon: 'ShieldCheck',title: 'Anomaly & Fraud Detection',      desc: 'Real-time unsupervised and semi-supervised models that flag outliers, suspicious transactions, and system failures before they escalate.', accent: '#DC2626' },
      { icon: 'Layers',     title: 'Deep Learning & Neural Nets',    desc: 'CNNs, RNNs, and transformer-based architectures for image recognition, time-series forecasting, and sequential data modelling.', accent: '#7C3AED' },
      { icon: 'BarChart3',  title: 'Time-Series Forecasting',        desc: 'ARIMA, Prophet, and LSTM-based forecasting for inventory planning, energy load prediction, financial modelling, and logistics optimisation.', accent: '#C9962E' },
      { icon: 'Settings',   title: 'MLOps & Model Monitoring',       desc: 'Automated retraining pipelines, data drift detection, A/B model testing, and dashboards — so your models keep performing after deployment.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Data Discovery & Audit',    desc: 'We assess your data sources, quality, volume, and labelling needs. We identify the highest-value prediction targets and define success KPIs.' },
      { step: '02', title: 'Feature Engineering',       desc: 'We clean, transform, and enrich raw data into ML-ready feature sets — including synthetic features, embeddings, and aggregations.' },
      { step: '03', title: 'Model Training & Selection', desc: 'We train multiple candidate models, benchmark them against each other, and select the best performer using cross-validation and held-out test sets.' },
      { step: '04', title: 'Deploy & Monitor',          desc: 'We containerise and deploy models as real-time APIs or batch pipelines, with dashboards tracking accuracy, drift, and business impact.' },
    ],
    use_cases: [
      { icon: 'Truck',       industry: 'Demand Forecasting',       desc: 'Predict SKU demand weeks ahead — reducing stockouts by 35% and overstock costs by 28% for retail and logistics companies.' },
      { icon: 'Users',       industry: 'Customer Churn Prediction', desc: 'Identify at-risk customers 30 days before they cancel — enabling targeted retention campaigns with 3× better conversion.' },
      { icon: 'ShieldCheck', industry: 'Fraud Detection',          desc: 'Flag fraudulent transactions in under 50ms with <0.1% false-positive rate — protecting fintech platforms and payment processors.' },
      { icon: 'BarChart3',   industry: 'Dynamic Pricing',          desc: 'Real-time price optimisation for e-commerce, airlines, and hospitality — maximising revenue per unit without losing volume.' },
      { icon: 'Building2',   industry: 'Predictive Maintenance',   desc: 'Detect equipment failure 72 hours in advance using sensor time-series data — cutting unplanned downtime by 60%.' },
      { icon: 'HeartPulse',  industry: 'Patient Risk Stratification', desc: 'Score patients by readmission risk and disease progression — helping hospitals prioritise care and reduce preventable admissions.' },
    ],
    tech_stack: [
      { name: 'Python',         color: '#3776AB' },
      { name: 'TensorFlow',     color: '#FF6F00' },
      { name: 'PyTorch',        color: '#EE4C2C' },
      { name: 'Scikit-learn',   color: '#F7931E' },
      { name: 'XGBoost',        color: '#117A8B' },
      { name: 'Apache Spark',   color: '#E25A1C' },
      { name: 'MLflow',         color: '#0194E2' },
      { name: 'Kubeflow',       color: '#326CE5' },
      { name: 'AWS SageMaker',  color: '#FF9900' },
      { name: 'Databricks',     color: '#FF3621' },
      { name: 'Feast',          color: '#2F6FB0' },
      { name: 'DVC',            color: '#945DD6' },
    ],
    benefits: [
      { icon: 'Cpu',        title: 'Purpose-built for your data',    desc: 'No off-the-shelf models. Every model is designed around your specific data distribution, business constraints, and accuracy requirements.' },
      { icon: 'Zap',        title: 'Fast from data to production',   desc: 'Our MLOps-first approach means models go from notebook to production API in days, not months — with automated CI/CD for retraining.' },
      { icon: 'Globe',      title: 'Cloud-agnostic deployment',      desc: 'We deploy on AWS, Azure, GCP, or your on-premise Kubernetes cluster — no lock-in, full portability.' },
      { icon: 'ShieldCheck',title: 'Explainable & auditable AI',     desc: 'SHAP values, LIME explanations, and audit trails built into every model — meeting regulatory requirements for finance, healthcare, and insurance.' },
    ],
    metaTitle: 'Machine Learning & Predictive Analytics — Max Global Software',
    metaDescription: 'Custom ML models for demand forecasting, churn prediction, fraud detection and more. End-to-end machine learning engineering by Max Global Software.',
  },

  /* ── 3. NLP & Computer Vision ────────────────────────────────────────── */
  'nlp-computer-vision': {
    title: 'NLP & Computer Vision',
    crumb: 'NLP & Vision',
    tagline:
      'Give your software the power to read, understand, and see. We build NLP pipelines and computer vision systems that extract meaning from text, images, documents, and video — at enterprise scale.',
    hero_image: '/images/services/hero/photo-1507146153580-69a1fe6d8aa1.jpg',
    overview_heading: 'Language and vision intelligence — embedded in your product',
    overview_body:
      'Natural Language Processing and Computer Vision are two of the most impactful AI capabilities available today. Together, they let software understand documents, classify images, extract structured data from unstructured content, and interpret the visual world in real time.\n\nWe build custom NLP and CV systems from scratch — from fine-tuned transformer models that extract entities and classify intent, to YOLO-based object detectors and OCR pipelines that digitise and categorise documents with 98%+ accuracy.\n\nWhether you need to automate document review, moderate user-generated content, read licence plates, or detect product defects on a production line — we engineer the solution end-to-end.',
    metrics: [
      { value: '98%',  label: 'OCR Extraction Accuracy' },
      { value: '150M+',label: 'Documents Processed' },
      { value: '30ms', label: 'Avg. Inference Latency' },
      { value: '25+',  label: 'NLP/CV Systems Shipped' },
    ],
    features: [
      { icon: 'FileText',   title: 'Document Understanding & OCR',   desc: 'Intelligent document processing that extracts structured fields from PDFs, forms, invoices, and contracts — going far beyond simple text extraction.', accent: '#2F6FB0' },
      { icon: 'MessageSquare', title: 'Text Classification & NER',   desc: 'Custom BERT and RoBERTa models for intent detection, named entity recognition, sentiment analysis, and multi-label document classification.', accent: '#10A37F' },
      { icon: 'Search',     title: 'Semantic Search',                 desc: 'Dense vector search over your document corpus using bi-encoders and cross-encoders — returning semantically relevant results, not just keyword matches.', accent: '#C9962E' },
      { icon: 'ShieldCheck',title: 'Content Moderation',             desc: 'Text and image moderation pipelines that detect hate speech, explicit content, misinformation, and policy violations — at social-media scale.', accent: '#DC2626' },
      { icon: 'Cpu',        title: 'Object Detection & Tracking',    desc: 'Real-time YOLOv8 and Detectron2 pipelines that detect, classify, and track objects in images and live video streams.', accent: '#7C3AED' },
      { icon: 'Layers',     title: 'Image Classification & Segmentation', desc: 'CNN and ViT models for product categorisation, medical image analysis, defect detection, and pixel-level semantic segmentation.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Dataset Curation',       desc: 'We assess your data, define labelling schemas, and either work with your existing annotated dataset or set up a labelling pipeline to create ground truth from scratch.' },
      { step: '02', title: 'Model Selection & Fine-tuning', desc: 'We select the best pre-trained backbone (BERT, RoBERTa, CLIP, YOLOv8, ViT) and fine-tune it on your domain-specific data for maximum accuracy.' },
      { step: '03', title: 'Evaluation & Edge Testing', desc: 'We benchmark on precision, recall, F1, and mAP — and stress-test against edge cases, adversarial inputs, and real-world noise.' },
      { step: '04', title: 'Production Deployment',  desc: 'We serve models via optimised ONNX/TorchScript runtimes, quantise for GPU/CPU efficiency, and wire up monitoring for accuracy and latency.' },
    ],
    use_cases: [
      { icon: 'FileText',   industry: 'Intelligent Document Processing', desc: 'Automate data extraction from invoices, purchase orders, insurance claims, and KYC documents — reducing manual data entry by 90%.' },
      { icon: 'ShieldCheck',industry: 'Content Moderation at Scale',    desc: 'Keep platforms safe with AI that reviews millions of text posts and images per day — flagging violations faster than any human team.' },
      { icon: 'Cpu',        industry: 'Visual Quality Inspection',      desc: 'Camera-based defect detection on manufacturing lines that catches surface faults, dimension errors, and assembly mistakes in real time.' },
      { icon: 'HeartPulse', industry: 'Medical Image Analysis',         desc: 'Assist radiologists with AI that segments tumours, flags anomalies in X-rays and MRIs, and prioritises urgent cases.' },
      { icon: 'Truck',      industry: 'Licence Plate Recognition',      desc: 'ANPR systems for parking management, toll automation, and vehicle access control — working in all weather and lighting conditions.' },
      { icon: 'ShoppingCart', industry: 'Product Catalogue Tagging',   desc: 'Automatic attribute extraction and tagging for e-commerce catalogues — reducing product onboarding time from days to minutes.' },
    ],
    tech_stack: [
      { name: 'spaCy',           color: '#09A3D5' },
      { name: 'Hugging Face',    color: '#FF9D00' },
      { name: 'OpenCV',          color: '#5C3EE8' },
      { name: 'YOLOv8',          color: '#FF6B35' },
      { name: 'Tesseract',       color: '#2F6FB0' },
      { name: 'BERT / RoBERTa',  color: '#FF4785' },
      { name: 'AWS Rekognition', color: '#FF9900' },
      { name: 'Google Vision AI', color: '#4285F4' },
      { name: 'Azure AI Vision', color: '#0078D4' },
      { name: 'FAISS',           color: '#059669' },
      { name: 'ONNX Runtime',    color: '#7C3AED' },
      { name: 'Detectron2',      color: '#DC2626' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'Real-time performance',         desc: 'Optimised inference pipelines that run in under 50ms — suitable for live video, interactive apps, and high-throughput batch processing.' },
      { icon: 'Globe',      title: 'Multilingual NLP',              desc: 'Models trained on 50+ languages using multilingual transformers — so your NLP works as well in Arabic or Mandarin as it does in English.' },
      { icon: 'ShieldCheck',title: 'Compliance-ready',              desc: 'GDPR-aware data pipelines, on-premise deployment options, and explainability tools for regulated industries.' },
      { icon: 'Cpu',        title: 'Scalable to any volume',        desc: 'From processing 1,000 documents a day to 1 million — we architect systems that scale horizontally without accuracy degradation.' },
    ],
    metaTitle: 'NLP & Computer Vision Development — Max Global Software',
    metaDescription: 'Custom NLP and computer vision solutions: document intelligence, OCR, object detection, content moderation. Built for production by Max Global Software.',
  },

  /* ── 4. Workflow Automation ──────────────────────────────────────────── */
  'workflow-automation': {
    title: 'Workflow Automation',
    crumb: 'Workflow Automation',
    tagline:
      'Eliminate manual work, accelerate operations, and free your team for high-value tasks. We design and deploy intelligent automation solutions — from RPA bots to AI-powered process orchestration — that work 24/7 without error.',
    hero_image: '/images/services/hero/photo-1485827404703-89b55fcc595e.jpg',
    overview_heading: 'Automate what slows you down — intelligently',
    overview_body:
      'Most businesses run on a hidden tax of repetitive manual work: data entry, report generation, approval routing, invoice processing, and system-to-system data transfers. This work is expensive, error-prone, and scalable only by hiring more people.\n\nWe engineer intelligent workflow automation that eliminates this tax. Combining Robotic Process Automation (RPA) for UI-level automation, API integration for system-to-system connectivity, and AI-powered document understanding for unstructured inputs — we build automation that works across your entire technology stack.\n\nOur automations handle exceptions intelligently, escalate to humans when needed, and provide full audit trails — giving you the compliance visibility your operations team demands.',
    metrics: [
      { value: '85%',  label: 'Reduction in Manual Processing Time' },
      { value: '99.5%',label: 'Process Accuracy Rate' },
      { value: '60+',  label: 'Workflows Automated' },
      { value: '3×',   label: 'ROI in First Year' },
    ],
    features: [
      { icon: 'Bot',        title: 'RPA Bots',                          desc: 'Software robots that interact with any UI — web apps, desktop tools, legacy systems — copying and transforming data without API access required.', accent: '#2F6FB0' },
      { icon: 'Search',     title: 'Process Mining & Discovery',         desc: 'AI-powered analysis of your event logs to map actual process flows, identify bottlenecks, and surface the highest-value automation candidates.', accent: '#10A37F' },
      { icon: 'FileText',   title: 'Intelligent Document Processing',    desc: 'Combine OCR, NLP, and business rules to automatically extract, validate, and route data from invoices, contracts, and forms.', accent: '#C9962E' },
      { icon: 'Network',    title: 'API Integration & iPaaS',            desc: 'Connect any two systems — CRM, ERP, HRMS, e-commerce, accounting — using pre-built connectors and custom API bridges.', accent: '#7C3AED' },
      { icon: 'Settings',   title: 'Business Rules Engine',              desc: 'Configurable decision engines that apply complex routing, approval, and escalation logic — without hardcoded if-else spaghetti.', accent: '#DC2626' },
      { icon: 'BarChart3',  title: 'Automation Analytics',               desc: 'Real-time dashboards showing bot performance, exception rates, SLA compliance, and ROI — so you always know what your automation is doing.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Process Audit',     desc: 'We map your existing workflows, measure time-per-task, identify error hotspots, and rank automation candidates by ROI potential.' },
      { step: '02', title: 'Bot Design & Build', desc: 'We design the automation architecture, build and configure bots, and integrate them with your source systems and business rules.' },
      { step: '03', title: 'Testing & Hardening', desc: 'We test every exception path, edge case, and failure scenario — then add retry logic, alerting, and human escalation workflows.' },
      { step: '04', title: 'Go-Live & Optimise', desc: 'We deploy to production, monitor bot performance in real time, and iteratively optimise throughput and accuracy based on live data.' },
    ],
    use_cases: [
      { icon: 'FileText',   industry: 'Invoice & AP Automation',         desc: 'Extract line items from supplier invoices, match to POs, flag discrepancies, and push approved invoices to your ERP — zero manual touchpoints.' },
      { icon: 'Users',      industry: 'HR Onboarding',                   desc: 'Automate account provisioning, document collection, policy acknowledgement, and day-one system access for every new hire.' },
      { icon: 'ShoppingCart',industry: 'Order Management',               desc: 'Sync orders across e-commerce, 3PL, and ERP systems in real time — eliminating manual re-keying and fulfilment delays.' },
      { icon: 'BarChart3',  industry: 'Regulatory Reporting',            desc: 'Auto-generate compliance reports from source systems, apply validation rules, and submit to regulators on schedule — every time.' },
      { icon: 'Database',   industry: 'Data Migration & Sync',           desc: 'Migrate and continuously sync data between legacy systems and modern platforms — with transformation rules and audit logs.' },
      { icon: 'MessageSquare', industry: 'Customer Notifications',       desc: 'Trigger personalised emails, SMS, and in-app messages based on system events — order shipped, appointment confirmed, payment received.' },
    ],
    tech_stack: [
      { name: 'UiPath',           color: '#FA4616' },
      { name: 'Automation Anywhere', color: '#FF6900' },
      { name: 'Make (Integromat)', color: '#6D00CC' },
      { name: 'n8n',              color: '#EA4B71' },
      { name: 'Zapier',           color: '#FF4A00' },
      { name: 'Robocorp',         color: '#00A9A5' },
      { name: 'Power Automate',   color: '#0066FF' },
      { name: 'Apache Airflow',   color: '#017CEE' },
      { name: 'Temporal',         color: '#7C3AED' },
      { name: 'Prefect',          color: '#1A1A2E' },
      { name: 'LangChain',        color: '#1C3B3A' },
      { name: 'FastAPI',          color: '#009688' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'Works while your team sleeps',      desc: 'Bots run 24/7/365 at consistent speed and accuracy — processing in minutes what used to take days.' },
      { icon: 'ShieldCheck',title: 'Full audit trail',                  desc: 'Every automated action is logged with timestamps, inputs, and outputs — providing the compliance evidence your auditors require.' },
      { icon: 'Globe',      title: 'System-agnostic',                   desc: 'Our automations work with any combination of modern SaaS, legacy desktop apps, APIs, and file-based systems — no system is off limits.' },
      { icon: 'Users',      title: 'Human-in-the-loop',                 desc: 'Smart exception handling escalates edge cases to humans seamlessly — so automation handles the 90% while your team handles the 10%.' },
    ],
    metaTitle: 'Workflow Automation & RPA Solutions — Max Global Software',
    metaDescription: 'Eliminate manual work with intelligent workflow automation, RPA bots, and AI-powered process orchestration. Built by Max Global Software.',
  },

  /* ── 5. AI Integration & Consulting ─────────────────────────────────── */
  'ai-integration-consulting': {
    title: 'AI Integration & Consulting',
    crumb: 'AI Consulting',
    tagline:
      'Not sure where AI fits in your business? We do. From strategic AI roadmaps and readiness assessments to hands-on integration of AI into your existing products — we guide you from uncertainty to production.',
    hero_image: '/images/services/hero/photo-1553877522-43269d4ea984.jpg',
    overview_heading: 'Strategy and execution — under one roof',
    overview_body:
      'Most AI initiatives fail not because the technology is wrong, but because the strategy is missing. Companies invest in AI tools without a clear use-case prioritisation, data strategy, or integration plan — and end up with expensive PoCs that never reach production.\n\nWe bridge that gap. Our AI consulting practice combines deep technical capability with pragmatic business thinking. We assess your current systems, data maturity, and team capabilities — then design an AI adoption roadmap that delivers measurable ROI in the shortest time.\n\nAnd unlike pure consultancies, we do not stop at the slide deck. Our engineering team takes the strategy all the way to production, integrating AI into your existing workflows, APIs, and platforms.',
    metrics: [
      { value: '100+', label: 'AI Strategy Engagements' },
      { value: '6wk',  label: 'Avg. Time to First PoC' },
      { value: '40+',  label: 'Countries Advised' },
      { value: '20',   label: 'Years of Tech Experience' },
    ],
    features: [
      { icon: 'Search',     title: 'AI Readiness Assessment',           desc: 'We audit your data infrastructure, team skills, existing tooling, and business processes to determine exactly where AI can deliver the fastest ROI.', accent: '#2F6FB0' },
      { icon: 'Layers',     title: 'Use Case Prioritisation',           desc: 'We identify and rank AI use cases by feasibility, data availability, and business impact — so you invest in the right things first.', accent: '#10A37F' },
      { icon: 'Cpu',        title: 'AI Architecture Design',            desc: 'We design the technical architecture for your AI systems: data pipelines, model serving infrastructure, API contracts, and integration touchpoints.', accent: '#C9962E' },
      { icon: 'Zap',        title: 'PoC & Pilot Development',           desc: 'We build production-quality proofs of concept in 4–6 weeks — demonstrating measurable value and de-risking the full investment.', accent: '#7C3AED' },
      { icon: 'Users',      title: 'Team Upskilling & Training',        desc: 'Hands-on workshops, training sessions, and embedded engineering to build internal AI capabilities that last beyond our engagement.', accent: '#DC2626' },
      { icon: 'ShieldCheck',title: 'AI Governance & Compliance',        desc: 'Responsible AI frameworks covering data privacy, model fairness, explainability, and regulatory compliance — for industries with strict requirements.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Discovery & Assessment',    desc: 'Two-week deep dive into your business, data landscape, team capabilities, and technology stack — resulting in a clear AI opportunity map.' },
      { step: '02', title: 'Strategy & Roadmap',        desc: 'We deliver a prioritised AI roadmap with use cases ranked by ROI, a 12-month implementation plan, and a total cost of ownership model.' },
      { step: '03', title: 'Pilot & Validate',          desc: 'We build and ship the highest-priority use case as a working PoC — proving the value hypothesis before full investment.' },
      { step: '04', title: 'Scale & Integrate',         desc: 'We productionise the pilot, integrate it into your existing systems, and begin executing the next items on the roadmap.' },
    ],
    use_cases: [
      { icon: 'Building2',  industry: 'Legacy System Modernisation',    desc: 'Overlay AI capabilities on top of existing ERP, CRM, and HRMS systems — without a disruptive rip-and-replace.' },
      { icon: 'BarChart3',  industry: 'Data Strategy & Architecture',   desc: 'Design the data infrastructure needed to support AI: data lakes, feature stores, streaming pipelines, and governance frameworks.' },
      { icon: 'Layers',     industry: 'AI Product Roadmap',             desc: 'Help product teams identify and sequence AI features that drive user engagement, retention, and willingness to pay.' },
      { icon: 'Search',     industry: 'Vendor & Model Evaluation',      desc: 'Independent assessment of AI vendors, foundation model providers, and MLOps platforms — so you buy on merit, not marketing.' },
      { icon: 'Users',      industry: 'AI Centre of Excellence',        desc: 'Help enterprises establish internal AI CoEs with the right processes, tooling, governance, and talent development programmes.' },
      { icon: 'ShieldCheck',industry: 'Compliance & Risk Advisory',     desc: 'Navigate GDPR, EU AI Act, HIPAA, and sector-specific AI regulations — with policies, audit trails, and model documentation.' },
    ],
    tech_stack: [
      { name: 'OpenAI',          color: '#10A37F' },
      { name: 'Anthropic',       color: '#D4734A' },
      { name: 'Azure AI',        color: '#0078D4' },
      { name: 'AWS AI Services', color: '#FF9900' },
      { name: 'Google AI',       color: '#4285F4' },
      { name: 'MLflow',          color: '#0194E2' },
      { name: 'Weights & Biases',color: '#FFBE00' },
      { name: 'LangChain',       color: '#1C3B3A' },
      { name: 'Vertex AI',       color: '#34A853' },
      { name: 'Databricks',      color: '#FF3621' },
      { name: 'Snowflake',       color: '#29B5E8' },
      { name: 'dbt',             color: '#FF694B' },
    ],
    benefits: [
      { icon: 'Globe',      title: 'Strategy meets engineering',        desc: 'We do not hand you a roadmap and walk away. The same team that advises you builds your AI — no strategy-to-delivery handoff gap.' },
      { icon: 'Zap',        title: 'Value in weeks, not years',        desc: 'Our PoC-first methodology means you see working AI in 4–6 weeks — validating the investment before scaling it.' },
      { icon: 'ShieldCheck',title: 'Vendor-independent advice',        desc: 'We are not resellers for any cloud or AI vendor. Our recommendations are driven by your needs, not our commercial relationships.' },
      { icon: 'Users',      title: 'Knowledge transfer built in',      desc: 'We document every decision, run training sessions, and pair with your team throughout — so capability stays in-house after we leave.' },
    ],
    metaTitle: 'AI Integration & Consulting Services — Max Global Software',
    metaDescription: 'Strategic AI consulting and hands-on integration. Readiness assessments, use-case roadmaps, PoC development, and production AI deployment by Max Global Software.',
  },

  /* ── 6. Web & Mobile Development ────────────────────────────────────── */
  'web-mobile-development': {
    title: 'Web & Mobile Development',
    crumb: 'Web & Mobile',
    tagline:
      'From high-performance web platforms to AI-powered iOS and Android apps — we design, build, and launch digital products that users love and businesses rely on. Shipped to 40+ countries.',
    hero_image: '/images/services/hero/photo-1551434678-e076c223a692.jpg',
    overview_heading: 'Full-stack digital products — web, mobile, and AI',
    overview_body:
      'Great software is not just functional — it is fast, accessible, maintainable, and built to scale. Our full-stack engineering team delivers web and mobile applications that meet all four criteria, using modern frameworks and cloud-native architecture.\n\nWe build everything from SaaS dashboards and customer portals to native iOS/Android apps and progressive web apps — always integrating AI capabilities where they create the most value: intelligent search, personalisation, predictive features, and conversational interfaces.\n\nWith 20+ years and 200+ projects delivered, we have the experience to anticipate the scale, compliance, and integration challenges that derail software projects — and the process discipline to prevent them.',
    metrics: [
      { value: '200+', label: 'Products Shipped' },
      { value: '40+',  label: 'Countries Deployed' },
      { value: '99.9%',label: 'Average Uptime SLA' },
      { value: '20',   label: 'Years of Engineering' },
    ],
    features: [
      { icon: 'Monitor',    title: 'React & Next.js Web Apps',          desc: 'High-performance, SEO-optimised web applications built with React, Next.js, and TypeScript — from marketing sites to complex SaaS platforms.', accent: '#2F6FB0' },
      { icon: 'Smartphone', title: 'iOS & Android Mobile Apps',         desc: 'Native and cross-platform mobile applications using Flutter and React Native — with AI features, offline support, and smooth 60fps UIs.', accent: '#10A37F' },
      { icon: 'Globe',      title: 'Progressive Web Apps',              desc: 'PWAs that work offline, install on any device, and deliver app-like experiences — without the friction of app store distribution.', accent: '#C9962E' },
      { icon: 'Server',     title: 'API & Backend Development',         desc: 'RESTful and GraphQL APIs built with Node.js, Python FastAPI, or Go — with authentication, rate limiting, caching, and observability baked in.', accent: '#7C3AED' },
      { icon: 'Cloud',      title: 'Cloud Architecture & DevOps',       desc: 'AWS, Azure, or GCP infrastructure designed for resilience and cost efficiency — with Terraform IaC, Docker, Kubernetes, and CI/CD pipelines.', accent: '#DC2626' },
      { icon: 'Zap',        title: 'AI-Enhanced Features',              desc: 'Integrate LLMs, recommendation engines, semantic search, and predictive analytics directly into your web or mobile product.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Discovery & Scoping',   desc: 'We define product requirements, user personas, technical constraints, and success metrics — producing a detailed technical specification before development begins.' },
      { step: '02', title: 'Design & Architecture', desc: 'UX wireframes, UI design, database schema, API contracts, and system architecture — documented and agreed before the first line of code is written.' },
      { step: '03', title: 'Agile Development',     desc: 'Two-week sprints with working software demonstrated at each milestone. Daily standups, weekly reviews, and transparent progress tracking.' },
      { step: '04', title: 'Launch & Scale',        desc: 'Production deployment, load testing, monitoring setup, and a 90-day post-launch support period — so go-live is an event, not a crisis.' },
    ],
    use_cases: [
      { icon: 'ShoppingCart',industry: 'E-commerce Platforms',         desc: 'Full-featured online stores with AI-powered search, personalised recommendations, and real-time inventory — handling millions of SKUs.' },
      { icon: 'Users',       industry: 'Customer & Partner Portals',   desc: 'Self-service portals that reduce support load, improve customer satisfaction, and automate account management workflows.' },
      { icon: 'Building2',   industry: 'Enterprise SaaS Products',     desc: 'Multi-tenant SaaS platforms with role-based access control, usage metering, billing integration, and white-labelling.' },
      { icon: 'HeartPulse',  industry: 'Healthcare & Telemedicine Apps', desc: 'HIPAA-compliant patient-facing applications: appointment booking, teleconsultation, medication tracking, and health records.' },
      { icon: 'BarChart3',   industry: 'Analytics & BI Dashboards',    desc: 'Real-time data visualisation platforms with drill-down filtering, custom reporting, and AI-powered anomaly highlighting.' },
      { icon: 'Smartphone',  industry: 'Fintech Mobile Apps',          desc: 'Secure, PCI-compliant mobile banking and payments applications with biometric auth, real-time notifications, and instant transfers.' },
    ],
    tech_stack: [
      { name: 'React',         color: '#61DAFB' },
      { name: 'Next.js',       color: '#000000' },
      { name: 'TypeScript',    color: '#3178C6' },
      { name: 'Flutter',       color: '#02569B' },
      { name: 'React Native',  color: '#61DAFB' },
      { name: 'Node.js',       color: '#339933' },
      { name: 'Python',        color: '#3776AB' },
      { name: 'PostgreSQL',    color: '#336791' },
      { name: 'Redis',         color: '#DC382D' },
      { name: 'Docker',        color: '#2496ED' },
      { name: 'Kubernetes',    color: '#326CE5' },
      { name: 'AWS / Azure',   color: '#FF9900' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'Speed without shortcuts',           desc: 'Agile delivery in two-week sprints — with clean, documented code that does not accumulate the technical debt that slows future development.' },
      { icon: 'ShieldCheck',title: 'Security-first engineering',        desc: 'OWASP Top-10 compliance, SAST/DAST scanning, secrets management, and penetration testing built into every project.' },
      { icon: 'Globe',      title: 'Built for global scale',            desc: 'Multi-region CDN, edge caching, database replication, and auto-scaling — so your product performs anywhere in the world.' },
      { icon: 'Cpu',        title: 'AI-ready architecture',             desc: 'Every system we build is designed to accommodate AI features — with the data pipelines, API hooks, and infrastructure needed to add intelligence later.' },
    ],
    metaTitle: 'Web & Mobile App Development — Max Global Software',
    metaDescription: 'Full-stack web and mobile development: React, Next.js, Flutter, React Native. AI-enhanced digital products shipped to 40+ countries by Max Global Software.',
  },

  /* ── 7. ERP Solutions ────────────────────────────────────────────────── */
  'erp-solutions': {
    title: 'ERP Solutions',
    crumb: 'ERP Solutions',
    tagline:
      'Enterprise resource planning that actually fits your business. Custom ERP development, Odoo & SAP implementation, and intelligent AI augmentation — delivered by a team with 20 years of enterprise engineering experience.',
    hero_image: '/images/services/hero/photo-1486406146926-c627a92ad1ab.jpg',
    overview_heading: 'ERP built for how your business actually works',
    overview_body:
      'Off-the-shelf ERP systems are built for the average company. Your business is not average. We build and implement ERP solutions that match your exact workflows, integration landscape, and compliance requirements — without forcing you to change your processes to fit the software.\n\nWhether you need a ground-up custom ERP built on modern web technologies, an Odoo or SAP implementation tailored to your industry, or an AI layer added to your existing enterprise system — we have the depth to deliver it.\n\nWith 200+ enterprise projects and clients in manufacturing, logistics, healthcare, retail, and professional services, we understand the complexity of enterprise operations — and the discipline required to implement systems that actually get adopted.',
    metrics: [
      { value: '200+', label: 'Enterprise Projects Delivered' },
      { value: '50%',  label: 'Avg. Process Efficiency Gain' },
      { value: '6mo',  label: 'Avg. Implementation Timeline' },
      { value: '20',   label: 'Years of Enterprise Engineering' },
    ],
    features: [
      { icon: 'Cpu',        title: 'Custom ERP Development',            desc: 'Bespoke ERP systems built from scratch on modern web stacks — designed around your exact business logic, not a pre-packaged feature set.', accent: '#2F6FB0' },
      { icon: 'Network',    title: 'ERP Integration & Middleware',       desc: 'Connect your ERP to CRM, HRMS, e-commerce, logistics, and banking systems via robust API middleware and event-driven architecture.', accent: '#10A37F' },
      { icon: 'Layers',     title: 'Module Development',                desc: 'Custom modules for procurement, inventory, manufacturing, finance, HR, and project management — extending your ERP without forking the core.', accent: '#C9962E' },
      { icon: 'Database',   title: 'Data Migration & Legacy Cutover',   desc: 'Safe migration from legacy systems with data cleansing, transformation, reconciliation, and a phased cutover plan that minimises disruption.', accent: '#7C3AED' },
      { icon: 'Cloud',      title: 'Cloud ERP & SaaS Hosting',          desc: 'Host your ERP on AWS, Azure, or GCP with high availability, automated backups, disaster recovery, and zero-downtime deployments.', accent: '#DC2626' },
      { icon: 'Bot',        title: 'AI-Powered ERP Intelligence',       desc: 'Add predictive analytics, intelligent search, anomaly detection, and natural language reporting on top of your existing ERP data.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Business Analysis',     desc: 'We map your current processes, pain points, and integration landscape — producing a detailed requirements document and implementation blueprint.' },
      { step: '02', title: 'System Design',         desc: 'We design the data model, module architecture, integration interfaces, and UX — reviewed and signed off before development begins.' },
      { step: '03', title: 'Phased Implementation', desc: 'We implement in phases — core modules first, then extensions and integrations — with UAT at each milestone and training for key users.' },
      { step: '04', title: 'Go-Live & Hypercare',   desc: 'We support the go-live transition with dedicated on-site and remote hypercare, resolving issues in real time and stabilising the system.' },
    ],
    use_cases: [
      { icon: 'Building2',  industry: 'Manufacturing & Production',      desc: 'BOM management, production scheduling, shop floor control, quality inspection, and OEE reporting — integrated with IoT and SCADA systems.' },
      { icon: 'Truck',      industry: 'Supply Chain & Logistics',        desc: 'End-to-end supply chain visibility: procurement, vendor management, warehouse management, freight tracking, and landed cost calculation.' },
      { icon: 'BarChart3',  industry: 'Finance & Accounting',            desc: 'Multi-entity, multi-currency general ledger, accounts payable/receivable, bank reconciliation, and consolidated financial reporting.' },
      { icon: 'Users',      industry: 'HR & Payroll Management',         desc: 'Employee lifecycle management from recruitment to offboarding, with payroll processing, leave management, and performance tracking.' },
      { icon: 'ShoppingCart',industry: 'Retail & Distribution',          desc: 'POS integration, omnichannel inventory, customer loyalty, and demand-driven replenishment — across multiple stores and warehouses.' },
      { icon: 'HeartPulse', industry: 'Healthcare Operations',           desc: 'Patient billing, insurance claims management, pharmacy inventory, equipment maintenance, and regulatory compliance reporting.' },
    ],
    tech_stack: [
      { name: 'Odoo 17',        color: '#875A7B' },
      { name: 'SAP',            color: '#0FAAFF' },
      { name: 'Microsoft Dynamics', color: '#00BCF2' },
      { name: 'Node.js',        color: '#339933' },
      { name: 'Python',         color: '#3776AB' },
      { name: 'PostgreSQL',     color: '#336791' },
      { name: 'Oracle DB',      color: '#F80000' },
      { name: 'Redis',          color: '#DC382D' },
      { name: 'Kafka',          color: '#231F20' },
      { name: 'AWS / Azure',    color: '#FF9900' },
      { name: 'Docker',         color: '#2496ED' },
      { name: 'Kubernetes',     color: '#326CE5' },
    ],
    benefits: [
      { icon: 'Cpu',        title: 'Fits your process, not vice versa', desc: 'We model the ERP around how your business actually works — not how a package vendor thinks it should work.' },
      { icon: 'ShieldCheck',title: 'Enterprise-grade security',         desc: 'Role-based access control, field-level encryption, full audit logging, and SOC 2 / ISO 27001 aligned data governance.' },
      { icon: 'Zap',        title: 'Phased delivery with early value',  desc: 'Core modules go live first — so you see ROI while the remaining scope is still being built.' },
      { icon: 'Globe',      title: 'Global multi-entity support',       desc: 'Multi-currency, multi-language, multi-entity architecture — built for businesses that operate across borders.' },
    ],
    metaTitle: 'ERP Solutions & Implementation — Max Global Software',
    metaDescription: 'Custom ERP development, Odoo & SAP implementation, and AI-powered enterprise applications. 200+ enterprise projects delivered by Max Global Software.',
  },

  /* ── 8. UI/UX Design ────────────────────────────────────────────────── */
  'ui-ux-design': {
    title: 'UI/UX Design',
    crumb: 'UI/UX Design',
    tagline:
      'Design that earns trust, drives conversions, and makes users come back. We create research-backed user experiences and pixel-perfect interfaces — from early wireframes to production-ready design systems.',
    hero_image: '/images/services/hero/photo-1545235617-9465d2a55698.jpg',
    overview_heading: 'Design that performs — not just design that looks good',
    overview_body:
      'Beautiful design that confuses users is a liability, not an asset. We design software interfaces that are intuitive by construction — built on UX research, usability testing, and data — so every design decision is grounded in how real users actually think and behave.\n\nOur design process starts with deep research: user interviews, competitor analysis, and behavioural analytics. We prototype rapidly, test with real users, and iterate until the experience is frictionless. Then we build a scalable design system that lets your engineering team ship consistently without reinventing components for every feature.\n\nEvery interface we deliver is accessible (WCAG 2.1 AA), responsive, and developer-ready — with detailed Figma handoff that eliminates ambiguity and speeds development.',
    metrics: [
      { value: '150+', label: 'Products Designed' },
      { value: '42%',  label: 'Avg. Conversion Rate Uplift' },
      { value: '4.8★', label: 'Avg. App Store Rating Post-Redesign' },
      { value: '3×',   label: 'Faster Dev Handoff with Design System' },
    ],
    features: [
      { icon: 'Search',   title: 'UX Research & Strategy',              desc: 'User interviews, surveys, heuristic evaluation, and analytics audits — building a deep understanding of your users before a single pixel is designed.', accent: '#2F6FB0' },
      { icon: 'Layers',   title: 'Design Systems & Component Libraries', desc: 'Scalable, token-based design systems in Figma — with documented components, interaction states, and responsive behaviour that developers can implement consistently.', accent: '#10A37F' },
      { icon: 'PenTool',  title: 'Wireframing & Prototyping',           desc: 'From low-fidelity sketches to interactive high-fidelity prototypes — validated with real users before engineering investment begins.', accent: '#C9962E' },
      { icon: 'ShieldCheck', title: 'Accessibility & Inclusive Design', desc: 'WCAG 2.1 AA compliance, screen-reader testing, keyboard navigation, and colour contrast validation — so your product works for everyone.', accent: '#7C3AED' },
      { icon: 'Zap',      title: 'Motion & Microinteraction Design',    desc: 'Purposeful animations and microinteractions that communicate state, guide attention, and add delight — without compromising performance.', accent: '#DC2626' },
      { icon: 'Code2',    title: 'Developer Handoff',                   desc: 'Annotated Figma files with spacing tokens, interaction notes, and asset exports — eliminating the design-to-development translation gap.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Research & Discovery',   desc: 'User interviews, analytics review, competitor analysis, and stakeholder workshops — producing a clear picture of user needs and design opportunities.' },
      { step: '02', title: 'Wireframes & IA',        desc: 'Information architecture, user flows, and low-fidelity wireframes — mapping the full experience skeleton before visual design begins.' },
      { step: '03', title: 'Visual Design & Prototype', desc: 'High-fidelity UI design in Figma with interactive prototype — reviewed in stakeholder sessions and tested with target users.' },
      { step: '04', title: 'Test, Refine & Handoff', desc: 'Usability testing, A/B validation, iteration, and production-ready Figma handoff with design system documentation for the engineering team.' },
    ],
    use_cases: [
      { icon: 'Monitor',    industry: 'B2B SaaS Redesign',             desc: 'Simplify complex dashboards and workflows — reducing user training time and increasing daily active usage.' },
      { icon: 'Smartphone', industry: 'Mobile App Design',             desc: 'Native iOS and Android app design that follows platform conventions while expressing a distinct brand identity.' },
      { icon: 'ShoppingCart',industry: 'E-commerce UX',               desc: 'Checkout flow optimisation, product discovery design, and personalised browsing experiences that lift conversion rates.' },
      { icon: 'BarChart3',  industry: 'Data Dashboards',               desc: 'Complex data visualisation made intuitive — from executive KPI dashboards to operational monitoring screens.' },
      { icon: 'Users',      industry: 'Onboarding & Activation Flows', desc: 'First-run experiences that get users to their first "aha moment" in minutes — dramatically reducing churn in the first 30 days.' },
      { icon: 'Palette',    industry: 'Brand Identity & Design System', desc: 'Logo, typography, colour palette, and a full component library — so every surface of your product feels cohesive and intentional.' },
    ],
    tech_stack: [
      { name: 'Figma',        color: '#F24E1E' },
      { name: 'Framer',       color: '#0055FF' },
      { name: 'Adobe XD',     color: '#FF61F6' },
      { name: 'Storybook',    color: '#FF4785' },
      { name: 'InVision',     color: '#FF3366' },
      { name: 'Lottie',       color: '#00DDB3' },
      { name: 'UserTesting',  color: '#F7941D' },
      { name: 'Hotjar',       color: '#F95738' },
      { name: 'Maze',         color: '#5C2FC2' },
      { name: 'Zeplin',       color: '#FDBD39' },
      { name: 'Principle',    color: '#E04CF0' },
      { name: 'Miro',         color: '#FFD02F' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'Research before pixels',            desc: 'We validate the design direction with real users before investing in high-fidelity design — eliminating expensive rework downstream.' },
      { icon: 'ShieldCheck',title: 'Accessibility built in, not bolted on', desc: 'WCAG compliance is part of our design process, not an afterthought — so your product is inclusive from day one.' },
      { icon: 'Cpu',        title: 'Design system that scales',         desc: 'Every project ends with a component library your team can extend — so new features ship faster with consistent quality.' },
      { icon: 'Globe',      title: 'Localisation-ready',                desc: 'RTL support, flexible text containers, and culturally aware design — so your product works for users worldwide.' },
    ],
    metaTitle: 'UI/UX Design Services — Max Global Software',
    metaDescription: 'Research-backed UX design, scalable design systems, and pixel-perfect UI. From wireframes to production-ready Figma handoff by Max Global Software.',
  },

  /* ── 9. RAG Systems ──────────────────────────────────────────────────── */
  'rag-systems': {
    title: 'Retrieval-Augmented Generation (RAG)',
    crumb: 'RAG Systems',
    tagline:
      'Stop hallucinations. Start trusting your AI. We build RAG systems that connect large language models to your private knowledge base — delivering accurate, grounded, source-cited answers from your own data.',
    hero_image: '/images/services/hero/photo-1620712943543-bcc4688e7485.jpg',
    overview_heading: 'LLMs that know your business — not just the internet',
    overview_body:
      'Out-of-the-box LLMs are trained on public data. They do not know your internal documentation, your product specifications, your compliance policies, or your customer history. When you ask them about your business, they guess — and they guess confidently.\n\nRetrieval-Augmented Generation solves this. RAG pipelines retrieve the most relevant chunks of your private knowledge at query time, inject them into the LLM context, and ground every response in your actual data — with citations that let users verify the source.\n\nWe build advanced RAG architectures: hybrid search (dense + sparse), re-ranking, query routing, multi-hop reasoning, and agentic RAG with tool use. Systems that handle millions of documents and return accurate answers in under 500ms.',
    metrics: [
      { value: '10×',  label: 'Faster Knowledge Retrieval' },
      { value: '95%',  label: 'Answer Accuracy on Domain Data' },
      { value: '500ms',label: 'End-to-End Query Latency' },
      { value: '50+',  label: 'RAG Pipelines Shipped' },
    ],
    features: [
      { icon: 'Database',   title: 'Document Ingestion & Chunking',     desc: 'Intelligent chunking strategies — semantic, sentence-window, and hierarchical — that preserve context and maximise retrieval precision.', accent: '#2F6FB0' },
      { icon: 'Search',     title: 'Hybrid Vector + Keyword Search',     desc: 'Combine dense vector similarity with sparse BM25 keyword matching — capturing both semantic intent and exact-match requirements.', accent: '#10A37F' },
      { icon: 'Layers',     title: 'Re-ranking & Context Compression',   desc: 'Cross-encoder re-ranking and context compression that selects the most relevant passages before sending to the LLM — improving accuracy and reducing cost.', accent: '#C9962E' },
      { icon: 'Network',    title: 'Agentic RAG with Tool Use',          desc: 'Multi-step reasoning agents that decompose complex questions, retrieve from multiple sources, and synthesise structured answers.', accent: '#7C3AED' },
      { icon: 'BrainCircuit',title: 'Multi-modal RAG',                  desc: 'Retrieve and reason over text, images, tables, and charts — so your RAG system understands your full document corpus, not just the text layer.', accent: '#DC2626' },
      { icon: 'BarChart3',  title: 'RAG Evaluation & Optimisation',      desc: 'RAGAS-based evaluation of faithfulness, answer relevance, and context recall — with systematic optimisation to hit your accuracy targets.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Knowledge Audit',       desc: 'We catalogue your document types, formats, volumes, and update frequency — designing an ingestion and indexing architecture that fits your corpus.' },
      { step: '02', title: 'Pipeline Design',        desc: 'We design the full RAG pipeline: embedding model, vector store, chunking strategy, retrieval algorithm, re-ranker, and LLM — with latency and cost targets.' },
      { step: '03', title: 'Build & Evaluate',       desc: 'We build the pipeline, create a golden evaluation set, and run RAGAS metrics to benchmark accuracy, faithfulness, and relevance.' },
      { step: '04', title: 'Deploy & Iterate',       desc: 'We deploy to production with monitoring, feedback collection, and a continuous improvement loop that improves accuracy over time.' },
    ],
    use_cases: [
      { icon: 'Building2',  industry: 'Enterprise Knowledge Assistant',  desc: 'Let employees search 10,000+ internal documents with natural language — getting accurate, cited answers in seconds.' },
      { icon: 'Scale',      industry: 'Legal Research & Contract Review', desc: 'Retrieve relevant precedents, clauses, and regulatory guidance from large legal corpora — in seconds instead of hours.' },
      { icon: 'HeartPulse', industry: 'Clinical Decision Support',       desc: 'Surface relevant clinical guidelines, drug interaction data, and patient history at the point of care — grounded in verified sources.' },
      { icon: 'GraduationCap',industry: 'EdTech Tutoring Systems',      desc: 'AI tutors that answer student questions with content grounded in course materials — not general internet knowledge.' },
      { icon: 'Briefcase',  industry: 'Customer Support Copilot',        desc: 'Give support agents an AI copilot that instantly retrieves the right answer from your knowledge base, SOPs, and product docs.' },
      { icon: 'ShoppingCart',industry: 'Product Catalogue Q&A',         desc: 'Let customers ask complex product questions and receive accurate, spec-grounded answers — reducing pre-purchase friction.' },
    ],
    tech_stack: [
      { name: 'LangChain',       color: '#1C3B3A' },
      { name: 'LlamaIndex',      color: '#C9962E' },
      { name: 'Pinecone',        color: '#1A2364' },
      { name: 'Weaviate',        color: '#00B08D' },
      { name: 'Qdrant',          color: '#DC143C' },
      { name: 'pgvector',        color: '#336791' },
      { name: 'OpenAI Embeddings', color: '#10A37F' },
      { name: 'Cohere Rerank',   color: '#D4734A' },
      { name: 'RAGAS',           color: '#FF6B35' },
      { name: 'FastAPI',         color: '#009688' },
      { name: 'Redis',           color: '#DC382D' },
      { name: 'Elasticsearch',   color: '#FEC514' },
    ],
    benefits: [
      { icon: 'ShieldCheck',title: 'Zero hallucination on your data',   desc: 'Every answer is grounded in retrieved documents and includes source citations — so users can trust and verify every response.' },
      { icon: 'Lock',       title: 'Your data stays private',           desc: 'Documents are indexed and searched within your infrastructure. Nothing is sent to external services without your explicit control.' },
      { icon: 'Zap',        title: 'Fast at any scale',                 desc: 'Optimised vector indexes and caching layers keep query latency under 500ms — even across corpora of millions of documents.' },
      { icon: 'Cpu',        title: 'Model-agnostic',                    desc: 'Works with any LLM — GPT-4o, Claude, Gemini, Llama, or your own fine-tuned model. Switch providers without rebuilding the pipeline.' },
    ],
    metaTitle: 'RAG System Development — Max Global Software',
    metaDescription: 'Build production-grade Retrieval-Augmented Generation systems that connect LLMs to your private knowledge base. Accurate, grounded, source-cited AI by Max Global Software.',
  },

  /* ── 10. AI Agents ───────────────────────────────────────────────────── */
  'ai-agents': {
    title: 'AI Agents & Agentic Systems',
    crumb: 'AI Agents',
    tagline:
      'Beyond chatbots — autonomous AI agents that reason, plan, and act. We build multi-agent systems that execute complex multi-step tasks, use tools, browse the web, write code, and deliver results with minimal human intervention.',
    hero_image: '/images/services/hero/photo-1555255707-c07966088b7b.jpg',
    overview_heading: 'AI that works, not just talks',
    overview_body:
      'A chatbot answers questions. An AI agent gets things done. Agentic systems combine the reasoning capability of large language models with tool use, memory, and planning — enabling software that can autonomously complete tasks that previously required a human expert.\n\nWe build agentic AI systems using LangGraph, AutoGen, and CrewAI — designing multi-agent architectures where specialised agents collaborate, check each other\'s work, and escalate to humans only when necessary.\n\nFrom autonomous research agents and code generation pipelines to customer onboarding agents and financial analysis bots — our agentic systems are production-hardened, observable, and built with the safety guardrails that real business deployment demands.',
    metrics: [
      { value: '30+',  label: 'Agentic Systems in Production' },
      { value: '80%',  label: 'Task Completion Without Human Review' },
      { value: '10×',  label: 'Faster Than Manual Workflows' },
      { value: '99%',  label: 'Agent Uptime SLA' },
    ],
    features: [
      { icon: 'Network',    title: 'Multi-Agent Orchestration',          desc: 'Supervisor-worker architectures using LangGraph and AutoGen — where specialised agents collaborate on complex tasks under a coordinating orchestrator.', accent: '#2F6FB0' },
      { icon: 'Zap',        title: 'Tool-Use & Function Calling',        desc: 'Agents that call APIs, query databases, execute code, search the web, send emails, and interact with any external system through structured tool use.', accent: '#10A37F' },
      { icon: 'BrainCircuit',title: 'Memory & State Management',         desc: 'Short-term working memory, long-term episodic memory, and shared agent state — enabling agents to maintain context across long-running tasks.', accent: '#C9962E' },
      { icon: 'Search',     title: 'Web Research & Data Gathering',      desc: 'Agents that autonomously research topics, scrape and synthesise information from multiple sources, and produce structured reports.', accent: '#7C3AED' },
      { icon: 'ShieldCheck',title: 'Human-in-the-Loop Safety',           desc: 'Configurable checkpoints where agents pause for human approval on high-stakes actions — with full action logs and rollback capabilities.', accent: '#DC2626' },
      { icon: 'BarChart3',  title: 'Agent Observability & Tracing',      desc: 'Full LangSmith and OpenTelemetry integration — so you can trace every agent decision, tool call, and LLM interaction in production.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Task Decomposition',    desc: 'We map the target workflow into subtasks, identify tool requirements, and design the agent graph — including decision points, loops, and human escalation triggers.' },
      { step: '02', title: 'Agent Design & Tooling', desc: 'We build and test individual agents and their tools, defining schemas, error handling, and retry logic for each component.' },
      { step: '03', title: 'Integration & Safety',   desc: 'We wire agents into the orchestration graph, implement guardrails, rate limiting, and human checkpoints — and run red-team testing for failure modes.' },
      { step: '04', title: 'Deploy & Monitor',       desc: 'Production deployment with full tracing, alerting, cost monitoring per agent run, and an improvement loop based on production outcomes.' },
    ],
    use_cases: [
      { icon: 'Search',     industry: 'Autonomous Research Agent',       desc: 'A market research agent that browses the web, reads documents, synthesises findings, and delivers a structured report in minutes.' },
      { icon: 'Code2',      industry: 'Code Generation & Review Agent',  desc: 'An engineering agent that reads requirements, writes code, runs tests, fixes failures, and opens PRs — autonomously.' },
      { icon: 'Users',      industry: 'Customer Onboarding Agent',       desc: 'An agent that guides new customers through account setup, document collection, verification, and first-use activation — end-to-end.' },
      { icon: 'BarChart3',  industry: 'Financial Analysis Agent',        desc: 'An agent that pulls financial data, runs analysis, generates commentary, and produces investor-ready reports on a scheduled cadence.' },
      { icon: 'Briefcase',  industry: 'Sales Prospecting Agent',         desc: 'An outbound agent that researches leads, personalises outreach, sends emails, follows up, and logs activity to your CRM.' },
      { icon: 'ShieldCheck',industry: 'Compliance Monitoring Agent',     desc: 'An agent that continuously monitors regulatory changes, flags impacts on your policies, and drafts updated documentation for review.' },
    ],
    tech_stack: [
      { name: 'LangGraph',    color: '#1C3B3A' },
      { name: 'AutoGen',      color: '#7C3AED' },
      { name: 'CrewAI',       color: '#DC143C' },
      { name: 'OpenAI GPT-4o',color: '#10A37F' },
      { name: 'Claude 3.5',   color: '#D4734A' },
      { name: 'LangSmith',    color: '#FF6B35' },
      { name: 'Temporal',     color: '#7C3AED' },
      { name: 'FastAPI',      color: '#009688' },
      { name: 'Redis',        color: '#DC382D' },
      { name: 'PostgreSQL',   color: '#336791' },
      { name: 'Docker',       color: '#2496ED' },
      { name: 'Kubernetes',   color: '#326CE5' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'From hours to minutes',             desc: 'Tasks that take a human expert 4 hours complete in under 5 minutes — with consistent quality and no fatigue.' },
      { icon: 'ShieldCheck',title: 'Safe by design',                    desc: 'Configurable guardrails, human checkpoints, and rollback capabilities mean agents operate within well-defined boundaries.' },
      { icon: 'Cpu',        title: 'Full observability',                desc: 'Every decision, tool call, and LLM prompt is logged and traceable — so you always know what the agent did and why.' },
      { icon: 'Globe',      title: 'Integrates with everything',        desc: 'Agents connect to your CRM, ERP, databases, APIs, and third-party services — working across your entire technology stack.' },
    ],
    metaTitle: 'AI Agents & Agentic Systems — Max Global Software',
    metaDescription: 'Build production-grade autonomous AI agents with LangGraph, AutoGen, and CrewAI. Multi-agent systems that reason, plan, and execute complex tasks.',
  },

  /* ── 11. Testing & QA ────────────────────────────────────────────────── */
  'testing-qa': {
    title: 'Testing & Quality Assurance',
    crumb: 'Testing & QA',
    tagline:
      'Ship software you can stand behind. Our QA engineers combine manual expertise with AI-powered test automation — finding bugs before your users do, and building the quality culture that keeps them away.',
    hero_image: '/images/services/hero/photo-1581090700227-1e37b190418e.jpg',
    overview_heading: 'Quality built into the process — not bolted on at the end',
    overview_body:
      'Quality assurance is not a phase at the end of development — it is an engineering discipline that runs throughout. Our QA team embeds into your development cycle from the start, building automated test suites in parallel with feature development and shifting left on defect detection.\n\nWe combine functional, regression, performance, security, and AI-specific testing into a unified quality pipeline — giving you confidence in every release, not just in the releases you specifically test.\n\nFor AI systems, we go further: evaluating LLM outputs for hallucination, bias, and consistency; benchmarking model accuracy on domain-specific datasets; and building the evaluation frameworks that make AI quality measurable and improvable.',
    metrics: [
      { value: '95%',  label: 'Test Coverage Achieved' },
      { value: '70%',  label: 'Reduction in Production Defects' },
      { value: '5×',   label: 'Faster Regression Cycles' },
      { value: '100+', label: 'Products Tested' },
    ],
    features: [
      { icon: 'TestTube2', title: 'Test Automation Frameworks',          desc: 'Selenium, Playwright, and Cypress automation suites that run full regression in minutes — with HTML reports, screenshots, and CI integration.', accent: '#2F6FB0' },
      { icon: 'Smartphone',title: 'Mobile App Testing',                  desc: 'Cross-device testing on real iOS and Android devices using Appium and BrowserStack — covering UI, gestures, push notifications, and offline mode.', accent: '#10A37F' },
      { icon: 'Zap',       title: 'Performance & Load Testing',          desc: 'k6 and JMeter load tests that simulate real traffic patterns — identifying bottlenecks, measuring throughput, and validating SLA targets.', accent: '#C9962E' },
      { icon: 'ShieldCheck',title: 'Security Testing (VAPT)',            desc: 'Vulnerability assessment and penetration testing covering OWASP Top 10, API security, authentication flaws, and injection vulnerabilities.', accent: '#DC2626' },
      { icon: 'BrainCircuit',title: 'AI Model Evaluation',              desc: 'LLM evaluation for hallucination, factual accuracy, bias, and consistency — using RAGAS, custom benchmarks, and red-team adversarial testing.', accent: '#7C3AED' },
      { icon: 'Repeat',    title: 'CI/CD Quality Gates',                 desc: 'Automated quality gates in your CI/CD pipeline that block deployments when coverage, performance, or security thresholds are not met.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'QA Strategy & Planning',  desc: 'We analyse your application, risk profile, and existing quality gaps — producing a test strategy, automation roadmap, and coverage targets.' },
      { step: '02', title: 'Framework Setup',          desc: 'We build the test automation infrastructure: framework selection, CI integration, reporting dashboards, and test data management.' },
      { step: '03', title: 'Test Development',         desc: 'We write automated tests for all critical paths, edge cases, and regression scenarios — with continuous review and maintenance as features evolve.' },
      { step: '04', title: 'Reporting & Improvement',  desc: 'We deliver defect reports, coverage metrics, and trend analysis — and run retrospectives to continuously improve quality processes.' },
    ],
    use_cases: [
      { icon: 'Monitor',    industry: 'Web App Regression Testing',      desc: 'Full-stack regression suites that run on every commit — catching UI regressions, API failures, and database issues before they reach production.' },
      { icon: 'Smartphone', industry: 'Mobile App Release Certification', desc: 'Pre-release certification testing across 50+ real devices — verifying functionality, performance, and platform guideline compliance.' },
      { icon: 'Zap',        industry: 'High-Traffic Load Testing',        desc: 'Validate that your platform handles peak traffic — Black Friday volumes, product launches, and viral events — without degrading.' },
      { icon: 'ShieldCheck',industry: 'Fintech Security Audit',          desc: 'Comprehensive security testing for payment flows, authentication systems, and financial data handling — meeting PCI-DSS requirements.' },
      { icon: 'BrainCircuit',industry: 'LLM Quality Evaluation',        desc: 'Systematic evaluation of AI assistants and RAG systems against domain-specific benchmarks — measuring accuracy, faithfulness, and safety.' },
      { icon: 'Building2',  industry: 'ERP Acceptance Testing',          desc: 'End-to-end UAT for ERP implementations — validating every business process, integration point, and data migration outcome.' },
    ],
    tech_stack: [
      { name: 'Playwright',    color: '#2EAD33' },
      { name: 'Cypress',       color: '#69D3A7' },
      { name: 'Selenium',      color: '#43B02A' },
      { name: 'Appium',        color: '#662D91' },
      { name: 'k6',            color: '#7D64FF' },
      { name: 'JMeter',        color: '#D22128' },
      { name: 'OWASP ZAP',     color: '#FF6633' },
      { name: 'BrowserStack',  color: '#F7A800' },
      { name: 'RAGAS',         color: '#FF6B35' },
      { name: 'Jest',          color: '#C21325' },
      { name: 'Pytest',        color: '#3776AB' },
      { name: 'GitHub Actions',color: '#2088FF' },
    ],
    benefits: [
      { icon: 'Zap',        title: 'Shift left — find bugs early',       desc: 'Defects found in development cost 10× less to fix than defects found in production. Our QA embeds early and keeps that ratio in your favour.' },
      { icon: 'ShieldCheck',title: 'Release with confidence',            desc: 'Automated regression suites mean every release is validated — not just the features you specifically test on that day.' },
      { icon: 'Cpu',        title: 'AI systems need AI evaluation',      desc: 'LLM outputs cannot be tested with traditional tools. Our AI evaluation frameworks measure what actually matters for intelligent systems.' },
      { icon: 'Globe',      title: 'Embedded, not outsourced',           desc: 'Our QA engineers work in your team, in your tools, in your sprints — not as an external gate at the end of development.' },
    ],
    metaTitle: 'Software Testing & QA Services — Max Global Software',
    metaDescription: 'AI-powered test automation, performance testing, security VAPT, and LLM evaluation. Embedded QA engineering by Max Global Software.',
  },

  /* ── 12. Blockchain ──────────────────────────────────────────────────── */
  'blockchain': {
    title: 'Blockchain & Web3 Solutions',
    crumb: 'Blockchain',
    tagline:
      'Build transparent, tamper-proof, and decentralised systems. We develop smart contracts, DeFi protocols, NFT platforms, and enterprise blockchain solutions on Ethereum, Solana, and Hyperledger.',
    hero_image: '/images/services/hero/photo-1639762681485-074b7f938ba0.jpg',
    overview_heading: 'Trust built into the technology — not the intermediary',
    overview_body:
      'Blockchain technology removes the need for trusted intermediaries by encoding trust directly into the protocol. Smart contracts execute automatically when conditions are met. Transactions are immutable and auditable by all parties. Assets can be tokenised and transferred globally without banks or brokers.\n\nWe build blockchain applications for enterprises that need auditability, transparency, and cross-party trust — and for Web3 teams building consumer-facing decentralised products. From DeFi protocols and NFT marketplaces to supply chain traceability and tokenised asset platforms — our engineers have shipped production blockchain systems across Ethereum, Polygon, Solana, and Hyperledger Fabric.\n\nWe combine smart contract security expertise with full-stack Web3 frontend engineering — delivering end-to-end blockchain products, not just contracts.',
    metrics: [
      { value: '30+',  label: 'Blockchain Projects Shipped' },
      { value: '$50M+',label: 'Value Transacted Through Our Systems' },
      { value: '0',    label: 'Critical Smart Contract Vulnerabilities Post-Audit' },
      { value: '5',    label: 'Blockchain Networks Deployed On' },
    ],
    features: [
      { icon: 'Code2',      title: 'Smart Contract Development',         desc: 'Solidity and Rust smart contracts for ERC-20/721/1155 tokens, DeFi protocols, DAOs, and custom business logic — rigorously tested and audited.', accent: '#2F6FB0' },
      { icon: 'Boxes',      title: 'NFT Platforms & Marketplaces',       desc: 'End-to-end NFT marketplace development with minting, listing, bidding, royalty enforcement, and IPFS/Arweave metadata storage.', accent: '#10A37F' },
      { icon: 'Network',    title: 'DeFi Protocol Development',          desc: 'Automated market makers, lending protocols, yield farming, staking contracts, and cross-chain bridges — built for security and capital efficiency.', accent: '#C9962E' },
      { icon: 'Truck',      title: 'Supply Chain Traceability',          desc: 'Enterprise blockchain on Hyperledger Fabric or Ethereum for end-to-end product provenance tracking — from raw material to retail shelf.', accent: '#7C3AED' },
      { icon: 'Database',   title: 'Tokenisation & Digital Assets',      desc: 'Tokenise real-world assets: real estate, commodities, equity, and receivables — with compliant issuance, transfer restrictions, and investor portals.', accent: '#DC2626' },
      { icon: 'ShieldCheck',title: 'Smart Contract Audit & Security',    desc: 'Manual and automated security audits of smart contracts — identifying reentrancy, integer overflow, access control, and oracle manipulation vulnerabilities.', accent: '#059669' },
    ],
    process_steps: [
      { step: '01', title: 'Architecture & Token Design',  desc: 'We design the on-chain data model, tokenomics, smart contract architecture, and off-chain integration layer — documented before any code is written.' },
      { step: '02', title: 'Smart Contract Development',   desc: 'We write, test, and deploy contracts using Hardhat or Foundry with 100% test coverage and formal verification where risk warrants it.' },
      { step: '03', title: 'Security Audit',               desc: 'We conduct internal and, for high-value protocols, third-party smart contract audits — remediating every finding before mainnet deployment.' },
      { step: '04', title: 'Frontend & Mainnet Launch',    desc: 'We build the Web3 frontend, integrate wallet connections (MetaMask, WalletConnect), and manage the mainnet deployment and post-launch monitoring.' },
    ],
    use_cases: [
      { icon: 'Briefcase',  industry: 'DeFi Lending & Borrowing',        desc: 'Permissionless lending protocols with algorithmic interest rates, collateral management, and liquidation engines.' },
      { icon: 'Boxes',      industry: 'NFT & Digital Collectibles',      desc: 'Branded NFT collections with custom minting mechanics, reveal logic, staking, and community governance.' },
      { icon: 'Truck',      industry: 'Food & Pharma Traceability',      desc: 'Immutable supply chain records from manufacturer to consumer — enabling instant product recalls and authenticity verification.' },
      { icon: 'Building2',  industry: 'Real Estate Tokenisation',        desc: 'Fractional ownership of commercial real estate through security tokens — with compliant issuance, dividend distribution, and secondary trading.' },
      { icon: 'ShieldCheck',industry: 'Decentralised Identity (DID)',    desc: 'Self-sovereign identity systems that let users control their credentials — without relying on centralised identity providers.' },
      { icon: 'Globe',      industry: 'Cross-Border Payments',           desc: 'Stablecoin payment rails for B2B cross-border transactions — settling in minutes instead of days, at a fraction of the cost.' },
    ],
    tech_stack: [
      { name: 'Ethereum',      color: '#627EEA' },
      { name: 'Solana',        color: '#9945FF' },
      { name: 'Polygon',       color: '#8247E5' },
      { name: 'Hyperledger',   color: '#2F6FB0' },
      { name: 'Solidity',      color: '#363636' },
      { name: 'Rust',          color: '#DEA584' },
      { name: 'Hardhat',       color: '#FFF100' },
      { name: 'Foundry',       color: '#FF6B35' },
      { name: 'ethers.js',     color: '#627EEA' },
      { name: 'IPFS',          color: '#65C2CB' },
      { name: 'The Graph',     color: '#6747ED' },
      { name: 'Chainlink',     color: '#375BD2' },
    ],
    benefits: [
      { icon: 'ShieldCheck',title: 'Security-first engineering',         desc: 'Every contract is tested, reviewed, and audited before mainnet deployment — we have a zero critical-vulnerability record across all launched protocols.' },
      { icon: 'Globe',      title: 'Multi-chain expertise',              desc: 'We build on Ethereum, Polygon, Solana, Avalanche, and Hyperledger — choosing the right network for your cost, speed, and compliance requirements.' },
      { icon: 'Zap',        title: 'Gas-optimised contracts',            desc: 'We optimise every contract for minimal gas consumption — reducing transaction costs for your users by up to 60%.' },
      { icon: 'Cpu',        title: 'Full-stack Web3',                    desc: 'Smart contracts, indexing, wallet integration, and frontend — one team delivers the complete product, not just the on-chain layer.' },
    ],
    metaTitle: 'Blockchain & Web3 Development — Max Global Software',
    metaDescription: 'Smart contracts, DeFi protocols, NFT platforms, and enterprise blockchain on Ethereum, Solana, and Hyperledger. Secure, audited blockchain development by Max Global Software.',
  },
};

/* ─── Icon map ───────────────────────────────────────────────────────────── */
const ICON_MAP = {
  Bot, BrainCircuit, MessageSquare, Mic, Network, Sliders,
  Building2, ShoppingCart, HeartPulse, Scale, Briefcase, GraduationCap,
  Zap, ShieldCheck, Globe, Cpu, CheckCircle2,
  Database, Search, Layers, Code2, Smartphone, Palette, TestTube2,
  FileText, BarChart3, Settings, Users, Lock, Repeat, Workflow,
  PenTool, Monitor, Server, Cloud, Truck, Boxes,
};

function resolveIcon(name, fallback = Bot) {
  return ICON_MAP[name] || fallback;
}

/* ─── Merge CMS data over defaults ─────────────────────────────────────── */
function mergePageData(slug, cmsData) {
  const base = DEFAULTS[slug] || {};
  if (!cmsData) return base;
  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(cmsData).filter(([, v]) => v !== null && v !== undefined && v !== '')
    ),
    metrics:       (Array.isArray(cmsData.metrics)       && cmsData.metrics.length)       ? cmsData.metrics       : base.metrics,
    features:      (Array.isArray(cmsData.features)      && cmsData.features.length)      ? cmsData.features      : base.features,
    process_steps: (Array.isArray(cmsData.process_steps) && cmsData.process_steps.length) ? cmsData.process_steps : base.process_steps,
    use_cases:     (Array.isArray(cmsData.use_cases)     && cmsData.use_cases.length)     ? cmsData.use_cases     : base.use_cases,
    tech_stack:    (Array.isArray(cmsData.tech_stack)    && cmsData.tech_stack.length)    ? cmsData.tech_stack    : base.tech_stack,
    benefits:      (Array.isArray(cmsData.benefits)      && cmsData.benefits.length)      ? cmsData.benefits      : base.benefits,
  };
}

/* ─── Sub-components ────────────────────────────────────────────────────── */

function MetricsBar({ metrics }) {
  return (
    <section className="bg-white border-b border-hairline">
      <div className="container mx-auto">
        <motion.div
          variants={staggerParent(0.1, 0)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-hairline"
        >
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              variants={cardPop}
              className="px-6 py-8 text-center group"
            >
              <div className="text-4xl sm:text-5xl font-bold text-navy-deep leading-none">
                <MetricCountUp value={m.value} />
              </div>
              <div className="mt-2 text-sm text-slatebody leading-snug">{m.label}</div>
              <div className="mx-auto mt-3 h-0.5 w-0 group-hover:w-10 bg-gold-gradient transition-all duration-500 rounded-full" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function OverviewSection({ heading, body, image }) {
  const paragraphs = (body || '').split('\n\n').filter(Boolean);
  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <SectionHeading
            eyebrow="What We Do"
            title={heading}
            align="left"
          />
          <div className="mt-8 space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-slatebody leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-8">
            <PrimaryButton icon={<ArrowRight className="h-4 w-4" />} onClick={() => {
              document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' });
            }}>
              Start Your AI Project
            </PrimaryButton>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="relative"
        >
          <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-brandblue/10 to-[#E8C25A]/10 blur-2xl" />
          <img
            src={image}
            alt="Generative AI illustration"
            className="relative rounded-2xl shadow-[0_24px_64px_rgba(47,111,176,0.18)] w-full object-cover aspect-[4/3]"
            loading="lazy"
          />
          <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-card px-4 py-3 flex items-center gap-2 border border-hairline">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-navy-deep">AI Systems Live in Production</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FeaturesSection({ features }) {
  return (
    <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(150deg,#0F192D 0%,#1F2D45 60%,#18304E 100%)' }}>
      <div className="absolute inset-0 opacity-[0.06]">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="featdots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="#fff" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#featdots)" />
        </svg>
      </div>

      <div className="container mx-auto relative">
        <SectionHeading
          eyebrow="Our Capabilities"
          title="What We Build For You"
          subtitle="Six core AI engineering capabilities — each battle-tested in production environments across 40+ countries."
          light
        />

        <motion.div
          variants={staggerParent(0.08, 0.15)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {features.map((f, i) => {
            const Icon = resolveIcon(f.icon);
            const accent = f.accent || '#2F6FB0';
            return (
              <motion.div
                key={i}
                variants={cardPop}
                className="group relative rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 hover:border-white/25 transition-all duration-300 overflow-hidden"
              >
                <div
                  className="absolute top-0 inset-x-0 h-0.5 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(90deg, ${accent}, ${accent}88)` }}
                />
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${accent}25` }}
                >
                  <Icon className="h-5 w-5" style={{ color: accent }} />
                </div>
                <h3 className="text-white font-semibold text-base leading-snug mb-2">{f.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#C9962E] hover:bg-[#b8851f] text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:shadow-[0_6px_20px_rgba(201,150,46,0.45)] hover:-translate-y-0.5"
          >
            Discuss this with us <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full border-2 border-white/60 hover:border-white text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300 hover:bg-white/10"
          >
            View All Services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function ProcessSection({ steps }) {
  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="How We Work"
          title="Our AI Delivery Process"
          subtitle="A structured, transparent process — from the first whiteboard session to a live AI system generating value for your business."
          align="center"
        />

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          <div className="hidden lg:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-hairline to-transparent" />
          {steps.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              transition={{ delay: i * 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-16 w-16 rounded-full bg-gradient-to-br from-brandblue to-[#1a4d80] flex items-center justify-center flex-shrink-0 shadow-[0_8px_24px_rgba(47,111,176,0.35)]">
                  <span className="text-white font-bold text-xl">{s.step}</span>
                </div>
              </div>
              <h3 className="text-navy-deep font-bold text-lg mb-2">{s.title}</h3>
              <p className="text-slatebody text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            type="button"
            onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-[#1a2d45] text-white text-sm font-semibold px-6 py-3 transition-all duration-300 hover:shadow-[0_6px_20px_rgba(31,45,69,0.45)] hover:-translate-y-0.5"
          >
            Start your project <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 rounded-full border-2 border-navy-deep hover:bg-navy-deep text-navy-deep hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            See Our Portfolio <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function UseCasesSection({ useCases }) {
  return (
    <section className="py-20 bg-sky-50">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Industry Applications"
          title="Where Generative AI Creates Real Impact"
          subtitle="From customer-facing chatbots to back-office automation — AI that pays for itself."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.07, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {useCases.map((u, i) => {
            const Icon = resolveIcon(u.icon, Briefcase);
            return (
              <Link
                key={i}
                to="/contact"
                className="group bg-white rounded-2xl border border-hairline p-6 shadow-card hover:shadow-[0_12px_40px_rgba(47,111,176,0.12)] hover:-translate-y-1 transition-all duration-300 block"
              >
                <motion.div variants={cardPop}>
                  <div className="h-10 w-10 rounded-xl bg-brandblue/10 flex items-center justify-center mb-4 group-hover:bg-brandblue/20 transition-colors duration-300">
                    <Icon className="h-5 w-5 text-brandblue" />
                  </div>
                  <h3 className="text-navy-deep font-semibold text-base mb-2">{u.industry}</h3>
                  <p className="text-slatebody text-sm leading-relaxed">{u.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-brandblue group-hover:text-[#C9962E] transition-colors">
                    Explore this use case <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                </motion.div>
              </Link>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 text-center"
        >
          <Link
            to="/industries"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brandblue hover:bg-brandblue text-brandblue hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            Explore All Industries <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function TechStackSection({ techStack }) {
  return (
    <section className="py-16 bg-white border-y border-hairline overflow-hidden">
      <div className="container mx-auto">
        <p className="text-center text-xs uppercase tracking-[0.2em] font-semibold text-brandblue mb-8">
          Technologies & Frameworks We Work With
        </p>
        <motion.div
          variants={staggerParent(0.05, 0)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="flex flex-wrap justify-center gap-3"
        >
          {techStack.map((t, i) => (
            <motion.span
              key={i}
              variants={cardPop}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold text-navy-deep transition-all duration-300 hover:-translate-y-0.5 cursor-default"
              style={{ borderColor: `${t.color}44`, background: `${t.color}0f` }}
            >
              <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: t.color }} />
              {t.name}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function BenefitsSection({ benefits }) {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Why MGS"
          title="What Sets Our AI Engineering Apart"
          subtitle="We are not an AI consultancy that hands you a slide deck. We are the engineers who ship it."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.08, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {benefits.map((b, i) => {
            const Icon = resolveIcon(b.icon, CheckCircle2);
            const accentColors = ['#2F6FB0', '#C9962E', '#059669', '#7C3AED'];
            const accent = accentColors[i % accentColors.length];
            return (
              <motion.div
                key={i}
                variants={cardPop}
                className="group rounded-2xl border border-hairline p-6 text-center hover:shadow-card transition-all duration-300 hover:-translate-y-1"
              >
                <div
                  className="h-14 w-14 rounded-2xl mx-auto flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${accent}15` }}
                >
                  <Icon className="h-6 w-6" style={{ color: accent }} />
                </div>
                <h3 className="text-navy-deep font-bold text-base mb-2">{b.title}</h3>
                <p className="text-slatebody text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTAs */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/why-mgs"
            className="inline-flex items-center gap-2 rounded-full border-2 border-navy-deep hover:bg-navy-deep text-navy-deep hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            About Max Global Software <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/our-team"
            className="inline-flex items-center gap-2 rounded-full border-2 border-brandblue hover:bg-brandblue text-brandblue hover:text-white text-sm font-semibold px-6 py-2.5 transition-all duration-300"
          >
            Meet Our Team <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Related Services section ──────────────────────────────────────────── */
function RelatedServicesSection({ currentSlug }) {
  const related = getRelated(currentSlug, 3);
  return (
    <section className="py-20 bg-sky-50 border-t border-hairline">
      <div className="container mx-auto">
        <SectionHeading
          eyebrow="Explore More"
          title="Related Services"
          subtitle="We offer end-to-end technology capabilities. Explore how our other services can support your goals."
          align="center"
        />

        <motion.div
          variants={staggerParent(0.1, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {related.map((svc) => {
            const Icon = resolveIcon(svc.icon, ArrowRight);
            return (
              <motion.div key={svc.slug} variants={cardPop}>
                <Link
                  to={`/services/${svc.slug}`}
                  className="group flex flex-col h-full bg-white rounded-2xl border border-hairline p-7 shadow-card hover:shadow-[0_16px_48px_rgba(47,111,176,0.13)] hover:-translate-y-1.5 transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-2xl bg-brandblue/10 flex items-center justify-center mb-5 group-hover:bg-brandblue/20 transition-colors duration-300">
                    <Icon className="h-6 w-6 text-brandblue" />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-brandblue mb-2">{svc.eyebrow}</p>
                  <h3 className="text-navy-deep font-bold text-lg leading-snug mb-3 flex-1">{svc.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slatebody group-hover:text-[#C9962E] transition-colors mt-auto">
                    Explore service <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="mt-10 text-center"
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full bg-navy-deep hover:bg-[#1a2d45] text-white text-sm font-semibold px-8 py-3 transition-all duration-300 hover:shadow-[0_8px_24px_rgba(15,25,45,0.35)] hover:-translate-y-0.5"
          >
            View All 12 Services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: cmsData, loading, notFound } = useServiceDetail(slug);

  const hasDefault = !!DEFAULTS[slug];
  const page = mergePageData(slug, cmsData);

  // SEO
  useEffect(() => {
    if (!page?.title) return;
    document.title = page.metaTitle || `${page.title} — Max Global Software`;
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', page.metaDescription || page.tagline || '');
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-32 pb-20">
        <Loader2 className="h-6 w-6 animate-spin text-slatebody/60" />
      </div>
    );
  }

  // True 404 only if no CMS data AND no hardcoded default for this slug
  if (notFound && !hasDefault) return <NotFound />;

  return (
    <article>
      {/* ── Hero ── */}
      <section className="relative pt-52 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-sky-100" />
        <div className="absolute inset-0 bg-hero-mesh bg-[length:200%_200%] animate-mesh-pan" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="svcdots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.1" fill="#2F6FB0" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#svcdots)" />
        </svg>

        <div className="container mx-auto relative text-center">
          <motion.nav
            aria-label="Breadcrumb"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex items-center justify-center gap-1 text-[12px] text-slatebody flex-wrap"
          >
            <Link to="/" className="hover:text-brandblue">Home</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
            <Link to="/services" className="hover:text-brandblue">Services</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
            <span className="font-semibold text-navy-deep">{page.crumb || page.title}</span>
          </motion.nav>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.1, ease: EASE }}
            className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-navy-deep max-w-3xl mx-auto"
          >
            {page.title}
          </motion.h1>

          {page.tagline && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.25, ease: EASE }}
              className="mt-5 max-w-2xl mx-auto text-slatebody text-sm sm:text-base leading-relaxed"
            >
              {page.tagline}
            </motion.p>
          )}

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            style={{ transformOrigin: 'center' }}
            className="mt-6 h-[2px] w-24 mx-auto rounded-full bg-gradient-to-r from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A]"
          />

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45, ease: EASE }}
            className="mt-7 flex flex-wrap items-center justify-center gap-4"
          >
            <PrimaryButton
              icon={<ArrowRight className="h-4 w-4" />}
              onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Start a Project
            </PrimaryButton>
            <BlueAnimatedLink to="/services">
              <ChevronLeft className="h-4 w-4" /> All Services
            </BlueAnimatedLink>
          </motion.div>
        </div>
      </section>

      {/* ── Metrics Bar ── */}
      {Array.isArray(page.metrics) && page.metrics.length > 0 && (
        <MetricsBar metrics={page.metrics} />
      )}

      {/* ── Overview ── */}
      <OverviewSection
        heading={page.overview_heading}
        body={page.overview_body}
        image={page.hero_image}
      />

      {/* ── Features / What We Build ── */}
      {Array.isArray(page.features) && page.features.length > 0 && (
        <FeaturesSection features={page.features} />
      )}

      {/* ── Process ── */}
      {Array.isArray(page.process_steps) && page.process_steps.length > 0 && (
        <ProcessSection steps={page.process_steps} />
      )}

      {/* ── Use Cases ── */}
      {Array.isArray(page.use_cases) && page.use_cases.length > 0 && (
        <UseCasesSection useCases={page.use_cases} />
      )}

      {/* ── Tech Stack ── */}
      {Array.isArray(page.tech_stack) && page.tech_stack.length > 0 && (
        <TechStackSection techStack={page.tech_stack} />
      )}

      {/* ── Benefits / Why MGS ── */}
      {Array.isArray(page.benefits) && page.benefits.length > 0 && (
        <BenefitsSection benefits={page.benefits} />
      )}

      {/* ── Related Services ── */}
      <RelatedServicesSection currentSlug={slug} />

      {/* ── CTA ── */}
      <CtaBand />
    </article>
  );
}
